import type { LocalTrayState } from '../lib/types';
import { getOpenGrafts, recordGraftCompletion } from '../lib/store';

interface Props {
  state: LocalTrayState;
  onChange: () => void;
}

export function LedgerPanel({ state, onChange }: Props) {
  const seedlings = state.seedlings.filter(s => !s.retracted_at);
  const grafts = getOpenGrafts();
  const attentionSources = seedlings.map(s => s.attention_source).filter(Boolean) as string[];
  const afters = state.aftertastes;

  return (
    <div className="ledger space-y-8 text-sm">
      <div>
        <div className="uppercase tracking-[1.5px] text-xs text-[var(--text-subtle)] mb-2">what this tray has held (cognition asset)</div>
        <div className="text-[15px] leading-tight">
          {seedlings.length} wisdom fragments planted after transitions.<br />
          {seedlings.filter(s => s.repair_readiness != null).length} carry explicit repair readiness scores (avg {Math.round(seedlings.reduce((a, s) => a + (s.repair_readiness ?? 45), 0) / Math.max(1, seedlings.length))}).<br />
          {seedlings.filter(s => s.accountability).length} name specific accountability.<br />
          {seedlings.filter(s => s.followups && s.followups.length).length} have concrete follow-ups recorded.<br />
          {state.tendings.length} tendings • {grafts.length} grafts • {afters.length} aftertastes ({afters.filter(a => a.shared_to_spread).length} shared).<br />
          Nothing leaves except what the circle exports or prints.
        </div>
      </div>

      <div>
        <div className="uppercase tracking-[1.5px] text-xs text-[var(--text-subtle)] mb-2">open grafts — repair without performance</div>
        {grafts.length === 0 && <div className="text-[var(--text-subtle)] text-xs">No grafts yet. When someone offers a small specific action it appears here.</div>}
        <div className="space-y-2 mt-1">
          {grafts.map((g, i) => {
            const meta = g.meta || {};
            const done = !!meta.completed_at;
            return (
              <div key={i} className="graft rounded-2xl px-4 py-3 flex flex-col gap-1">
                <div>{g.tending.content}</div>
                <div className="text-xs text-[var(--text-subtle)]">offered by {g.member?.tendril_name} • within {meta.timeframe_days || '?'} days</div>
                {!done && (
                  <button onClick={() => { recordGraftCompletion(g.tending.id); onChange(); }} className="self-start text-xs underline mt-1">mark received / felt (private to you)</button>
                )}
                {done && <div className="text-[var(--gold)] text-xs">marked received</div>}
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <div className="uppercase tracking-[1.5px] text-xs text-[var(--text-subtle)] mb-2">where attention has come from</div>
        {attentionSources.length === 0 && <div className="text-xs text-[var(--text-subtle)]">No attention sources logged yet. They ground the noticing without turning it into performance.</div>}
        <ul className="text-xs space-y-1 text-[var(--text-muted)]">
          {attentionSources.slice(0, 6).map((a, i) => <li key={i}>• {a}</li>)}
        </ul>
      </div>

      <div>
        <div className="uppercase tracking-[1.5px] text-xs text-[var(--text-subtle)] mb-2">recovery time (aftertastes)</div>
        {afters.length === 0 && <div className="text-xs text-[var(--text-subtle)]">Recovery notes are private by default. They make visible that care has a pace and a body.</div>}
        <div className="space-y-2">
          {afters.filter(a => a.recovery_note || a.bodily_feeling).slice(0, 4).map((a, i) => (
            <div key={i} className="aftertaste px-3 py-2 rounded-xl text-xs">
              {a.recovery_note || a.bodily_feeling}
              {a.shared_to_spread && <span className="ml-2 text-[var(--lavender)]">(shared)</span>}
            </div>
          ))}
        </div>
      </div>

      <div className="text-[10px] text-[var(--text-subtle)] pt-2 border-t border-[var(--border)]">
        Consent is recorded with every plant and tending. You can retract your own contributions. The tray itself can be archived. This is not a permanent archive of your life.
      </div>
    </div>
  );
}
